import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  DollarSign,
  FileText,
  Building2,
  Download,
  Calendar,
  BarChart3,
  ChevronDown,
  Loader2
} from 'lucide-react';
import XLSX from 'xlsx-js-style';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useApp } from '../../context/AppContext';
import { useBrokerData } from '../../context/BrokerDataContext';
import './Reports.css';

function Reports() {
  // Get broker-specific transactions from BrokerDataContext
  const { transactions, isLoading } = useBrokerData();
  // Get global data from AppContext
  const { brokerUsers, brokers } = useApp();
  const [dateRange, setDateRange] = useState('7days');
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef(null);
  const exportBtnRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target) &&
          exportBtnRef.current && !exportBtnRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update dropdown position when opened
  useEffect(() => {
    if (showExportDropdown && exportBtnRef.current) {
      const rect = exportBtnRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [showExportDropdown]);

  // Get date range filter
  const getDateRangeStart = () => {
    const now = new Date();
    switch (dateRange) {
      case '7days':
        return new Date(now.setDate(now.getDate() - 7));
      case '30days':
        return new Date(now.setDate(now.getDate() - 30));
      case '90days':
        return new Date(now.setDate(now.getDate() - 90));
      case 'year':
        return new Date(now.getFullYear(), 0, 1);
      default:
        return new Date(now.setDate(now.getDate() - 7));
    }
  };

  // Filter transactions by date range
  const filteredTransactions = useMemo(() => {
    const startDate = getDateRangeStart();
    return transactions.filter(t => {
      const txnDate = new Date(t.createdAt);
      return txnDate >= startDate;
    });
  }, [transactions, dateRange]);

  // Calculate report data based on filtered transactions
  const completedTransactions = filteredTransactions.filter(t => t.status === 'completed');
  const totalVolume = completedTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const averageTransaction = completedTransactions.length > 0
    ? totalVolume / completedTransactions.length
    : 0;

  const matchRate = filteredTransactions.length > 0
    ? (filteredTransactions.filter(t => t.verificationStatus === 'matched').length / filteredTransactions.length) * 100
    : 0;

  const rejectionRate = filteredTransactions.length > 0
    ? (filteredTransactions.filter(t => t.status === 'rejected').length / filteredTransactions.length) * 100
    : 0;

  // Broker breakdown based on filtered transactions
  const brokerStats = brokers.map(broker => {
    const brokerTxns = filteredTransactions.filter(t => t.broker === broker.name);
    const completedTxns = brokerTxns.filter(t => t.status === 'completed');
    return {
      name: broker.name,
      totalTransactions: brokerTxns.length,
      completedTransactions: completedTxns.length,
      volume: completedTxns.reduce((sum, t) => sum + (t.amount || 0), 0),
      status: broker.status,
    };
  });

  // Get today's transactions for daily report
  const getTodayTransactions = () => {
    const today = new Date();
    const todayStr = today.toDateString();
    return transactions.filter(t => {
      const txnDate = new Date(t.createdAt);
      return txnDate.toDateString() === todayStr;
    });
  };

  // Format date for filename
  const getFormattedDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Export daily transaction report using xlsx library
  const exportDailyTransactionReport = () => {
    const todayTxns = getTodayTransactions();

    if (todayTxns.length === 0) {
      alert('No transactions found for today.');
      return;
    }

    // Define headers
    const headers = ['Ref No', 'User Name', 'Broker', 'Amount (PKR)', 'Txn ID', 'Status'];

    // Helper to get bank transaction ID from AI extracted data
    const getBankTxnId = (txn) => {
      const extracted = txn.aiVerificationResult?.extracted ||
                        txn.aiVerificationResult?.extracted_data ||
                        txn.aiExtractedData || {};
      return extracted.transaction_id ||
             extracted.transactionId ||
             extracted.reference_id ||
             extracted.referenceId ||
             'N/A';
    };

    // Prepare data rows
    const dataRows = todayTxns.map(txn => [
      txn.referenceNumber || 'N/A',
      txn.userName || 'N/A',
      txn.broker || 'N/A',
      txn.amount || 0,
      getBankTxnId(txn),
      txn.status || 'N/A'
    ]);

    // Create worksheet with headers first
    const worksheetData = [headers, ...dataRows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    worksheet['!cols'] = [
      { wch: 28 },  // Ref No
      { wch: 32 },  // User Name
      { wch: 22 },  // Broker
      { wch: 18 },  // Amount
      { wch: 24 },  // Txn ID
      { wch: 18 }   // Status
    ];

    // Apply header styles (blue background, white text, bold)
    const headerStyle = {
      fill: { fgColor: { rgb: '1E40AF' } },
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } }
      }
    };

    // Apply styles to header cells
    const headerCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1'];
    headerCells.forEach(cell => {
      if (worksheet[cell]) {
        worksheet[cell].s = headerStyle;
      }
    });

    // Apply data cell styles
    const dataCellStyle = {
      alignment: { horizontal: 'left', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: 'E5E7EB' } },
        bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
        left: { style: 'thin', color: { rgb: 'E5E7EB' } },
        right: { style: 'thin', color: { rgb: 'E5E7EB' } }
      }
    };

    // Apply alternating row colors and styles to data cells
    for (let row = 2; row <= dataRows.length + 1; row++) {
      const isEvenRow = row % 2 === 0;
      const rowStyle = {
        ...dataCellStyle,
        fill: { fgColor: { rgb: isEvenRow ? 'F8FAFC' : 'FFFFFF' } }
      };

      ['A', 'B', 'C', 'D', 'E', 'F'].forEach(col => {
        const cellRef = `${col}${row}`;
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = rowStyle;

          // Special styling for Amount column (center align)
          if (col === 'D') {
            worksheet[cellRef].s = { ...rowStyle, alignment: { horizontal: 'right', vertical: 'center' } };
          }

          // Special styling for Status column based on value
          if (col === 'F') {
            const status = worksheet[cellRef].v;
            let statusColor = '6B7280'; // gray default
            if (status === 'completed') statusColor = '059669'; // green
            else if (status === 'pending_review' || status === 'pending') statusColor = 'D97706'; // amber
            else if (status === 'rejected') statusColor = 'DC2626'; // red

            worksheet[cellRef].s = {
              ...rowStyle,
              font: { color: { rgb: statusColor }, bold: true },
              alignment: { horizontal: 'center', vertical: 'center' }
            };
          }
        }
      });
    }

    // Set row height for header
    worksheet['!rows'] = [{ hpt: 28 }];

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Transactions');

    // Generate filename
    const filename = `Daily_Transaction_Report_${getFormattedDate()}.xlsx`;

    // Download file (use bookType xlsx for styling support)
    XLSX.writeFile(workbook, filename, { bookType: 'xlsx' });
  };

  const handleExport = (type) => {
    setShowExportDropdown(false);

    switch (type) {
      case 'transactions':
        exportDailyTransactionReport();
        break;
      case 'verification':
        alert('Verification Report export coming soon...');
        break;
      case 'broker':
        alert('Broker Report export coming soon...');
        break;
      case 'financial':
        alert('Financial Report export coming soon...');
        break;
      default:
        alert('Export type not supported');
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="reports-page">
        <div className="loading-container">
          <Loader2 size={48} className="spinning" />
          <h3>Loading Reports...</h3>
          <p>Generating analytics data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Reports & Analytics</h1>
          <p>View transaction statistics and generate reports</p>
        </div>
        <div className="header-actions">
          <div className="date-filter">
            <Calendar size={18} />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="select"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <div className="export-dropdown-wrapper">
            <button
              ref={exportBtnRef}
              className="export-btn"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              <Download size={18} />
              <span>Export Report</span>
              <ChevronDown size={16} className={showExportDropdown ? 'rotate' : ''} />
            </button>
            {showExportDropdown && ReactDOM.createPortal(
              <div
                className="export-dropdown-portal"
                ref={exportDropdownRef}
                style={{
                  position: 'fixed',
                  top: dropdownPosition.top,
                  right: dropdownPosition.right,
                  zIndex: 99999
                }}
              >
                <button onClick={() => handleExport('transactions')}>
                  <FileText size={16} />
                  <span>Daily Transaction Report</span>
                </button>
                <button onClick={() => handleExport('verification')}>
                  <BarChart3 size={16} />
                  <span>Verification Report</span>
                </button>
                <button onClick={() => handleExport('broker')}>
                  <Building2 size={16} />
                  <span>Broker Report</span>
                </button>
                <button onClick={() => handleExport('financial')}>
                  <DollarSign size={16} />
                  <span>Financial Report</span>
                </button>
              </div>,
              document.body
            )}
          </div>
        </div>
      </div>

      {/* Export Reports Section */}
      <Card className="export-card-top">
        <CardHeader>
          <CardTitle>Export Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="export-options">
            <button className="export-option" onClick={() => handleExport('transactions')}>
              <FileText size={24} />
              <div className="export-info">
                <span className="export-title">Daily Transaction Report</span>
                <span className="export-desc">Today's transactions (Excel/CSV)</span>
              </div>
            </button>
            <button className="export-option" onClick={() => handleExport('verification')}>
              <BarChart3 size={24} />
              <div className="export-info">
                <span className="export-title">Verification Report</span>
                <span className="export-desc">Matched vs mismatched analysis</span>
              </div>
            </button>
            <button className="export-option" onClick={() => handleExport('broker')}>
              <Building2 size={24} />
              <div className="export-info">
                <span className="export-title">Broker Report</span>
                <span className="export-desc">Performance by broker</span>
              </div>
            </button>
            <button className="export-option" onClick={() => handleExport('financial')}>
              <DollarSign size={24} />
              <div className="export-info">
                <span className="export-title">Financial Report</span>
                <span className="export-desc">Revenue and volume analysis</span>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="reports-grid">
        {/* Transaction Status Breakdown */}
        <Card className="chart-card">
          <CardHeader>
            <CardTitle>Transaction Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="status-breakdown">
              <div className="status-item">
                <div className="status-bar">
                  <div
                    className="status-fill completed"
                    style={{
                      width: `${(completedTransactions.length / Math.max(filteredTransactions.length, 1)) * 100}%`
                    }}
                  ></div>
                </div>
                <div className="status-info">
                  <span className="status-label">Completed</span>
                  <span className="status-value">{completedTransactions.length}</span>
                </div>
              </div>

              <div className="status-item">
                <div className="status-bar">
                  <div
                    className="status-fill pending"
                    style={{
                      width: `${(filteredTransactions.filter(t => t.status === 'pending_review').length / Math.max(filteredTransactions.length, 1)) * 100}%`
                    }}
                  ></div>
                </div>
                <div className="status-info">
                  <span className="status-label">Pending Review</span>
                  <span className="status-value">
                    {filteredTransactions.filter(t => t.status === 'pending_review').length}
                  </span>
                </div>
              </div>

              <div className="status-item">
                <div className="status-bar">
                  <div
                    className="status-fill rejected"
                    style={{
                      width: `${(filteredTransactions.filter(t => t.status === 'rejected').length / Math.max(filteredTransactions.length, 1)) * 100}%`
                    }}
                  ></div>
                </div>
                <div className="status-info">
                  <span className="status-label">Rejected</span>
                  <span className="status-value">
                    {filteredTransactions.filter(t => t.status === 'rejected').length}
                  </span>
                </div>
              </div>
            </div>

            <div className="chart-legend">
              <div className="legend-item">
                <span className="legend-color completed"></span>
                <span>Completed</span>
              </div>
              <div className="legend-item">
                <span className="legend-color pending"></span>
                <span>Pending</span>
              </div>
              <div className="legend-item">
                <span className="legend-color rejected"></span>
                <span>Rejected</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Verification Analysis */}
        <Card className="chart-card">
          <CardHeader>
            <CardTitle>Verification Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="verification-stats">
              <div className="verification-circle">
                <svg viewBox="0 0 36 36" className="circular-chart">
                  <path
                    className="circle-bg"
                    d="M18 2.0845
                       a 15.9155 15.9155 0 0 1 0 31.831
                       a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="circle"
                    strokeDasharray={`${matchRate}, 100`}
                    d="M18 2.0845
                       a 15.9155 15.9155 0 0 1 0 31.831
                       a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="circle-text">
                  <span className="percentage">{matchRate.toFixed(0)}%</span>
                  <span className="label">Match Rate</span>
                </div>
              </div>

              <div className="verification-breakdown">
                <div className="breakdown-item matched">
                  <span className="breakdown-value">
                    {filteredTransactions.filter(t => t.verificationStatus === 'matched').length}
                  </span>
                  <span className="breakdown-label">Matched</span>
                </div>
                <div className="breakdown-item mismatched">
                  <span className="breakdown-value">
                    {filteredTransactions.filter(t => t.verificationStatus === 'mismatched').length}
                  </span>
                  <span className="breakdown-label">Mismatched</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Broker Performance */}
        <Card className="broker-performance-card">
          <CardHeader>
            <CardTitle>Broker Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="broker-table">
              <div className="table-header">
                <span>Broker</span>
                <span>Transactions</span>
                <span>Volume</span>
                <span>Status</span>
              </div>
              {brokerStats.map((broker, index) => (
                <div key={index} className="table-row">
                  <span className="broker-name">
                    <Building2 size={16} />
                    {broker.name}
                  </span>
                  <span className="broker-txns">
                    {broker.completedTransactions}/{broker.totalTransactions}
                  </span>
                  <span className="broker-volume">
                    PKR {broker.volume.toLocaleString()}
                  </span>
                  <span className={`broker-status ${broker.status}`}>
                    {broker.status}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card className="quick-stats-card">
          <CardHeader>
            <CardTitle>Quick Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="quick-stats-list">
              <div className="quick-stat">
                <span className="stat-label">Average Transaction</span>
                <span className="stat-value">PKR {averageTransaction.toLocaleString()}</span>
              </div>
              <div className="quick-stat">
                <span className="stat-label">Success Rate</span>
                <span className="stat-value">
                  {filteredTransactions.length > 0
                    ? ((completedTransactions.length / filteredTransactions.length) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
              <div className="quick-stat">
                <span className="stat-label">Rejection Rate</span>
                <span className="stat-value">{rejectionRate.toFixed(1)}%</span>
              </div>
              <div className="quick-stat">
                <span className="stat-label">Total Registered Users</span>
                <span className="stat-value">{brokerUsers.length}</span>
              </div>
              <div className="quick-stat">
                <span className="stat-label">Active Brokers</span>
                <span className="stat-value">
                  {brokers.filter(b => b.status === 'active').length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}

export default Reports;
